import { useAdmin } from 'dashboard/composables/useAdmin';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Redacts a full name (person or company) by replacing every even-positioned
 * word (2nd, 4th, 6th, …) with a fixed placeholder.
 *
 * Examples:
 *   "Alice Smith"                     → "Alice ****"
 *   "Alice Mary Smith"                → "Alice **** Smith"
 *   "WIN OCEAN REAL ESTATE COMPANY LTD" → "WIN **** REAL **** COMPANY ****"
 */
export function redactName(fullName) {
  if (!fullName) return '—';
  const parts = fullName.trim().split(/\s+/);
  return parts.map((word, i) => ((i + 1) % 2 === 0 ? '****' : word)).join(' ');
}

/**
 * Shared PII masking for Ardhisasa data. Admins see unredacted values;
 * everyone else sees masked values. Reused across the Users module and the
 * land-process detail views.
 */
export function usePiiRedaction() {
  const { isAdmin } = useAdmin();

  function redactPersonName(name) {
    if (!name) return '—';
    if (isAdmin.value) return name;
    return redactName(name);
  }

  function redactEmail(email) {
    if (!email) return '—';
    if (isAdmin.value) return email;
    const [local, domain] = email.split('@');
    if (!domain) return `${local.slice(0, 3)}***`;
    return `${local.slice(0, 3)}***@${domain}`;
  }

  function redactPhone(phone) {
    if (!phone) return '—';
    if (isAdmin.value) return phone;
    const str = String(phone);
    return `${str.slice(0, 7)}***${str.slice(-2)}`;
  }

  function redactId(id) {
    if (!id) return '—';
    if (isAdmin.value) return id;
    const str = String(id);
    if (str.length <= 6) return '***';
    return `${str.slice(0, 4)}***${str.slice(-2)}`;
  }

  function redactKra(kra) {
    if (!kra) return '—';
    if (isAdmin.value) return kra;
    const str = String(kra);
    return `${str.slice(0, 3)}***${str.slice(-3)}`;
  }

  function redactEmployeeNum(num) {
    if (!num) return '—';
    if (isAdmin.value) return num;
    const str = String(num);
    if (str.length <= 4) return '***';
    return `${str.slice(0, 2)}***${str.slice(-2)}`;
  }

  return {
    isAdmin,
    redactPersonName,
    redactEmail,
    redactPhone,
    redactId,
    redactKra,
    redactEmployeeNum,
  };
}
